import { Grid2 as Grid, Box, Stack, Typography } from "@mui/material"
import { SectionTitle } from "../../components/SectionTitle"
import useModalTrigger from "magik-react-hooks/useModalTrigger"
import { useNavigate } from "react-router-dom"
import {
  useDocumento,
  useDocumentoUpdate,
  useDocumentoHistory,
  useDocumentoDelete,
  useDocumentoSetResponsabili,
} from "../../hooks/documenti"
import { useSnackbarMessage } from "../../hooks/snackbar"
import { useDesiredHeight } from "../../hooks/measuring"
import { DataField, DataFieldLabel, DataFieldLink, DataRow } from "../../components/DataField"
import { useParams } from "react-router-dom"
import { useTranslation } from "react-i18next"
import ChiaviAccessoDisplay from "../../components/ChiaviAccessoDisplay"
import RecordInfo from "../../components/RecordInfo"
import DeleteBar from "../../components/DeleteBar"
import DialogConfirm from "../../components/DialogConfirm"
import DialogForm from "../../components/DialogForm"
import DocumentoForm from "../../components/forms/DocumentoForm"
import DocumentoFormCatalogazione from "../../components/forms/DocumentoFormCatalogazione"
import DocumentoFormChiavi from "../../components/forms/DocumentoFormChiavi"
import { Documento, HistoryBag, WithResponsabili } from "../../types"
import ResponsabiliForm from "../../components/forms/ResponsabiliForm"
import useUserCanWriteAndDelete from "../../hooks/permissions"
import { useIsMobile } from "../../hooks/mobile"

export default function DocumentoDettaglio() {
  const { id } = useParams()
  const { t, i18n } = useTranslation()
  const { data: documento, refetch } = useDocumento(parseInt(id as string))
  const navigate = useNavigate()
  const userCanWriteAndDelete = useUserCanWriteAndDelete()
  const isMobile = useIsMobile()

  const historyBag = useDocumentoHistory(id as string)

  const [updateModalState, updateModalActions] = useModalTrigger()
  const [updateCatalogazioneModalState, updateCatalogazioneModalActions] = useModalTrigger()
  const [updateChiaviModalState, updateChiaviModalActions] = useModalTrigger()
  const [deleteModalState, deleteModalActions] = useModalTrigger()
  const [updateModalStateResponsabili, updateModalActionsResponsabili] = useModalTrigger()

  const { mutateAsync: remove } = useDocumentoDelete()
  const { mutateAsync: update } = useDocumentoUpdate()
  const { mutateAsync: setResponsabili } = useDocumentoSetResponsabili(id as string)
  const showMessage = useSnackbarMessage()

  const { ref } = useDesiredHeight(24)
  return (
    <>
      <Grid
        container
        spacing={2}
        sx={{
          overflow: "auto",
          height: "100%",
          p: 3,
          pt: 2,
        }}
      >
        <Grid
          size={{
            xs: 12,
            xl: 6,
          }}
        >
          <Stack
            sx={{
              height: "fit-content",
              bgcolor: "white",
              borderRadius: 1,
              boxShadow: 1,
            }}
          >
            <SectionTitle
              first
              title={t("sections.identificazione")}
              onEdit={() => {
                updateModalActions.open()
              }}
            />
            <Box px={3} pb={2}>
              <DataRow>
                <DataField label={t("documento.data_produzione")} value={documento?.data_produzione} size={4} />
                <DataField label={t("documento.cronologia_da")} value={documento?.cronologia_da} size={4} />
                <DataField label={t("documento.cronologia_a")} value={documento?.cronologia_a} size={4} />
              </DataRow>

              <DataRow>
                <DataField label={t("documento.titolo_proprio")} value={documento?.titolo_proprio} size={8} />
                <DataField label={t("documento.complemento_titolo")} value={documento?.complemento_titolo} size={8} />
              </DataRow>

              <DataRow>
                <DataField label={t("fondo.genere")} value={documento?.genere_data?.map((item) => item.termine)} size={8} />
              </DataRow>

              <DataRow>
                <Grid size={6}>
                  <DataFieldLabel label={t("general.supporti")} />
                  <div>
                    {documento?.supporti_data?.map((supporto, index) => (
                      <span key={supporto.id}>
                        <DataFieldLink
                          display="inline-flex"
                          link={`/supporti/${supporto.id}`}
                          key={supporto.id}
                          value={supporto.segnatura}
                        />
                        {index < (documento.supporti_data || []).length - 1 && ", "}
                      </span>
                    ))}
                  </div>
                </Grid>
                <Grid size={6}>
                  <DataFieldLabel label={t("general.fondo")} />
                  <div>
                    <DataFieldLink
                      display="inline-flex"
                      link={`/fondi/${documento?.fondo.id}`}
                      value={documento?.fondo.segnatura+ " - " + documento?.fondo.denominazione}
                    />
                  </div>
                </Grid>
              </DataRow>
            </Box>

            <SectionTitle
              first
              title={t("sections.contenuto_e_copyright")}
              onEdit={() => {
                updateCatalogazioneModalActions.open()
              }}
            />
            <Box px={3} pb={2}>
              <DataRow>
                <DataField label={t("documento.titolo_attribuito")} value={documento?.titolo_attribuito} size={8} />
                <DataField label={t("documento.note_titolo")} value={documento?.note_titolo} size={8} />
                <DataField label={t("documento.abstract")} value={documento?.abstract} size={8} />
                <DataField
                  label={t("documento.notizie_stor_critiche")}
                  value={documento?.notizie_stor_critiche}
                  size={8}
                />
              </DataRow>
              <DataRow last>
                <DataField
                  label={t("documento.vincoli_modalita_consultazione")}
                  value={documento?.vincoli_modalita_consultazione}
                  size={8}
                />
                <DataField label={t("documento.copyright")} value={documento?.copyright} size={8} />
                <DataField
                  label={t("documento.bibliografia_sitografia")}
                  value={documento?.bibliografia_sitografia}
                  size={8}
                />
                <DataField label={t("documento.limiti_utilizzo")} value={documento?.limiti_utilizzo} size={8} boolean />
              </DataRow>
            </Box>
          </Stack>
        </Grid>

        <Grid
          size={{
            xs: 12,
            xl: 6,
          }}
        >
          <Stack
            flexGrow={1}
            sx={{
              height: "100%",
              bgcolor: "white",
              borderRadius: 1,
              boxShadow: 1,
            }}
          >
            <SectionTitle
              first
              title={t("sections.catalogazione_chiavi_accesso")}
              onEdit={() => {
                updateChiaviModalActions.open()
              }}
            />
            <Box px={3} pb={2}>
              <ChiaviAccessoDisplay item={documento} />
            </Box>

            <SectionTitle
              first
              title={t("sections.catalogazione_responsabili")}
              onEdit={() => {
                updateModalActionsResponsabili.open()
              }}
            />
            <Box px={3} py={2}>
              <Stack spacing={1}>
                {documento?.responsabili_data?.map((responsabile) => (
                  <>
                    <DataField
                      key={responsabile.id}
                      label={responsabile.ruolo_data?.termine ?? ""}
                      value={`${responsabile.persona_data?.nome}`}
                      size={8}
                    />
                    <Typography variant="body2" color="text.secondary" mt={0}>
                      {responsabile.note}
                    </Typography>
                  </>
                ))}
              </Stack>
            </Box>
          </Stack>
        </Grid>

        <Grid
          size={{
            xs: 12,
            xl: 12,
          }}
        >
          <Stack
            ref={ref}
            sx={{
              bgcolor: "grey.200",
              borderRadius: 1,
              boxShadow: 1,
            }}
          >
            <SectionTitle bgcolor="grey.700" info />
            <Stack sx={{ p: 2 }} flexGrow={1}>
              <RecordInfo
                first_update={documento?.first_update}
                last_update={documento?.last_update}
                historyBag={historyBag as HistoryBag}
              />
            </Stack>
            {userCanWriteAndDelete && !isMobile && documento?.can_delete && (
              <DeleteBar title={t("documento.cta_elimina")} onDelete={() => deleteModalActions.open(documento)} />
            )}
          </Stack>
        </Grid>
      </Grid>
      <DialogForm<Documento>
        open={updateModalState.isOpen}
        handleClose={updateModalActions.close}
        FormComponent={DocumentoForm}
        title={t("documento.cta_modifica")}
        save={(values) => {
          return update(values).then(() => {
            updateModalActions.close()
            refetch()
            showMessage(t("documento.esito_modifica_ok"))
          })
        }}
        item={documento}
        hasCancel
      />

      <DialogForm<Documento>
        open={updateChiaviModalState.isOpen}
        handleClose={updateChiaviModalActions.close}
        FormComponent={DocumentoFormChiavi}
        title={t("documento.cta_modifica_chiavi")}
        save={(values) => {
          return update(values).then(() => {
            updateChiaviModalActions.close()
            refetch()
            showMessage(t("documento.esito_modifica_ok"))
          })
        }}
        item={documento}
        hasCancel
      />

      <DialogForm<Documento>
        open={updateCatalogazioneModalState.isOpen}
        handleClose={updateCatalogazioneModalActions.close}
        FormComponent={DocumentoFormCatalogazione}
        title={t("documento.cta_modifica_catalogazione")}
        hasDoubleLanguage
        save={(values) => {
          console.log(values)
          return update(values).then(() => {
            updateCatalogazioneModalActions.close()
            refetch()
            showMessage(t("documento.esito_modifica_ok"))
          })
        }}
        item={documento}
        hasCancel
      />

      <DialogForm<WithResponsabili>
        open={updateModalStateResponsabili.isOpen}
        handleClose={updateModalActionsResponsabili.close}
        FormComponent={ResponsabiliForm}
        title={t("general.cta_modifica_allegati")}
        save={(values) => {
          return setResponsabili({ responsabili_data: values.responsabili_data }).then(() => {
            updateModalActionsResponsabili.close()
            refetch()
            showMessage(t("supporto.esito_modifica_ok"))
          })
        }}
        item={{ responsabili_data: documento?.responsabili_data }}
        hasCancel
      />

      <DialogConfirm
        open={deleteModalState.isOpen}
        handleClose={deleteModalActions.close}
        title={t("documento.cta_elimina")}
        subtitle={
          <span>
            {t("documento.cta_subtitle_elimina")} <b>"{deleteModalState.value?.denominazione}"</b>&nbsp;?
          </span>
        }
        textConfirm={t("general.elimina")}
        onConfirm={() => {
          return remove(deleteModalState.value.id)
            .then(() => {
              deleteModalActions.close()
              showMessage(t("documento.esito_elimina_ok"))
              navigate(`/${i18n.language}/documenti`)
            })
            .catch(() => {
              deleteModalActions.close()
              showMessage(t("general.esito_elimina_errore"), "error")
            })
        }}
      />
    </>
  )
}
